import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Key, Plus, Pencil, Trash2, Copy, RefreshCw, RotateCcw, Loader2, Check,
  Wallet, IndianRupee, TrendingUp, ArrowUpRight, ArrowDownRight, BarChart3,
  Zap, Clock, Activity
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell
} from "recharts";

const STATIC_PROVIDER_FALLBACK = [
  { slug: "openai", name: "OpenAI", color: "#10A37F" },
  { slug: "anthropic", name: "Anthropic", color: "#F97316" },
  { slug: "google", name: "Google", color: "#4285F4" },
  { slug: "groq", name: "Groq", color: "#8B5CF6" },
  { slug: "nvidia", name: "NVIDIA NIM", color: "#76B900" },
];

const PROVIDER_COLORS = {
  openai: "#10A37F", anthropic: "#F97316", google: "#4285F4", groq: "#8B5CF6", nvidia: "#76B900"
};

function formatINR(amount) {
  if (amount === undefined || amount === null) return "0.00";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export default function ApiManagementPage({ api }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [form, setForm] = useState({ name: "", allowed_providers: [], rate_limit_per_min: 60, monthly_cap_usd: 100 });
  const [saving, setSaving] = useState(false);
  const [newKeyValue, setNewKeyValue] = useState(null);
  const [newKeyDialogOpen, setNewKeyDialogOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupKeyId, setTopupKeyId] = useState(null);
  const [topupAmount, setTopupAmount] = useState("");
  const [topupLoading, setTopupLoading] = useState(false);
  const [spendingSummary, setSpendingSummary] = useState(null);
  const [selectedKeySpending, setSelectedKeySpending] = useState(null);
  const [spendingData, setSpendingData] = useState(null);
  const [spendingPeriod, setSpendingPeriod] = useState("daily");
  const [providers, setProviders] = useState(STATIC_PROVIDER_FALLBACK);
  const [spendingLoading, setSpendingLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("keys");

  const fetchKeys = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/api-keys`);
      setKeys(res.data);
    } catch { toast.error("Failed to load API keys"); }
    finally { setLoading(false); }
  }, [api]);

  const fetchSpendingSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${api}/api-keys/spending/summary`);
      setSpendingSummary(res.data);
    } catch { /* silently fail */ }
  }, [api]);

  useEffect(() => { fetchKeys(); fetchSpendingSummary(); }, [fetchKeys, fetchSpendingSummary]);

  useEffect(() => {
    axios.get(`${api}/providers`)
      .then(r => {
        const list = (r.data || []).map(p => ({
          slug: p.slug,
          name: p.name,
          color: p.accent || "#6B7280",
        }));
        if (list.length) setProviders(list);
      })
      .catch(() => { /* keep static fallback */ });
  }, [api]);

  const openCreate = () => {
    setEditKey(null);
    setForm({ name: "", allowed_providers: providers.map(p => p.slug), rate_limit_per_min: 60, monthly_cap_usd: 100 });
    setDialogOpen(true);
  };

  const openEdit = (k) => {
    setEditKey(k);
    setForm({ name: k.name, allowed_providers: k.allowed_providers || [], rate_limit_per_min: k.rate_limit_per_min || 60, monthly_cap_usd: k.monthly_cap_usd || 100 });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (editKey) {
        await axios.put(`${api}/api-keys/${editKey.id}`, form);
        toast.success("API key updated");
      } else {
        const res = await axios.post(`${api}/api-keys`, form);
        setNewKeyValue(res.data.full_key);
        setNewKeyDialogOpen(true);
        toast.success("API key created!");
      }
      setDialogOpen(false);
      fetchKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${api}/api-keys/${deleteId}`);
      toast.success("API key deleted");
      setDeleteId(null);
      fetchKeys();
    } catch { toast.error("Failed to delete"); }
  };

  const handleRegenerate = async (id) => {
    try {
      const res = await axios.post(`${api}/api-keys/${id}/regenerate`);
      setNewKeyValue(res.data.full_key);
      setNewKeyDialogOpen(true);
      toast.success("Key regenerated!");
      fetchKeys();
    } catch { toast.error("Failed to regenerate"); }
  };

  const toggleEnabled = async (k) => {
    try {
      await axios.put(`${api}/api-keys/${k.id}`, { enabled: !k.enabled });
      toast.success(k.enabled ? "Key disabled" : "Key enabled");
      fetchKeys();
    } catch { toast.error("Failed"); }
  };

  const copyKey = async () => {
    if (newKeyValue) {
      try { await navigator.clipboard.writeText(newKeyValue); } catch {
        const ta = document.createElement("textarea"); ta.value = newKeyValue; ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      }
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleProvider = (slug) => {
    setForm(f => {
      const has = f.allowed_providers.includes(slug);
      return { ...f, allowed_providers: has ? f.allowed_providers.filter(s => s !== slug) : [...f.allowed_providers, slug] };
    });
  };

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!amount || amount <= 0) { toast.error("Enter a valid amount"); return; }
    setTopupLoading(true);
    try {
      const res = await axios.post(`${api}/api-keys/${topupKeyId}/topup`, { amount_inr: amount });
      toast.success(`Added ${formatINR(amount)} INR. New balance: ${formatINR(res.data.new_balance_inr)} INR`);
      setTopupOpen(false);
      setTopupAmount("");
      fetchKeys();
      fetchSpendingSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Top-up failed");
    } finally { setTopupLoading(false); }
  };

  const openSpending = async (keyId) => {
    setSelectedKeySpending(keyId);
    setActiveTab("spending");
    loadSpending(keyId, spendingPeriod);
    loadTransactions(keyId);
  };

  const loadSpending = async (keyId, period) => {
    setSpendingLoading(true);
    try {
      const res = await axios.get(`${api}/api-keys/${keyId}/spending?period=${period}`);
      setSpendingData(res.data);
    } catch { toast.error("Failed to load spending data"); }
    finally { setSpendingLoading(false); }
  };

  const loadTransactions = async (keyId) => {
    setTxLoading(true);
    try {
      const res = await axios.get(`${api}/api-keys/${keyId}/transactions?limit=50`);
      setTransactions(res.data.transactions || []);
    } catch { /* silently fail */ }
    finally { setTxLoading(false); }
  };

  const handlePeriodChange = (period) => {
    setSpendingPeriod(period);
    if (selectedKeySpending) loadSpending(selectedKeySpending, period);
  };

  const totalBalance = keys.reduce((sum, k) => sum + (k.balance_inr || 0), 0);

  return (
    <div className="space-y-6" data-testid="api-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold" data-testid="page-title">API Management</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage API keys, wallet balances, and track spending</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchKeys(); fetchSpendingSummary(); }} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Refresh</span>
          </Button>
          <Button size="sm" onClick={openCreate} data-testid="create-api-key-btn"><Plus className="w-4 h-4 mr-1" /> Create Key</Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card data-testid="total-balance-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Wallet className="w-4 h-4 text-emerald-600" />
              <p className="text-xs text-muted-foreground font-medium">Total Balance</p>
            </div>
            <p className="text-xl font-bold text-emerald-700" data-testid="total-balance-value">{formatINR(totalBalance)} INR</p>
          </CardContent>
        </Card>
        <Card data-testid="today-spend-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              <p className="text-xs text-muted-foreground font-medium">Today</p>
            </div>
            <p className="text-xl font-bold" data-testid="today-spend-value">{formatINR(spendingSummary?.today_spend_inr || 0)} INR</p>
          </CardContent>
        </Card>
        <Card data-testid="week-spend-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-4 h-4 text-violet-600" />
              <p className="text-xs text-muted-foreground font-medium">This Week</p>
            </div>
            <p className="text-xl font-bold" data-testid="week-spend-value">{formatINR(spendingSummary?.week_spend_inr || 0)} INR</p>
          </CardContent>
        </Card>
        <Card data-testid="month-spend-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <IndianRupee className="w-4 h-4 text-amber-600" />
              <p className="text-xs text-muted-foreground font-medium">This Month</p>
            </div>
            <p className="text-xl font-bold" data-testid="month-spend-value">{formatINR(spendingSummary?.month_spend_inr || 0)} INR</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="tabs-list">
          <TabsTrigger value="keys" data-testid="tab-keys">API Keys</TabsTrigger>
          <TabsTrigger value="spending" data-testid="tab-spending" disabled={!selectedKeySpending}>Spending</TabsTrigger>
        </TabsList>

        <TabsContent value="keys" className="space-y-4">
          {/* Usage hint */}
          <Card className="border-dashed" data-testid="usage-hint-card">
            <CardContent className="p-4">
              <p className="text-sm font-medium mb-2">Using the Unified API</p>
              <div className="overflow-x-auto">
                <code className="text-xs font-mono bg-muted p-2 rounded block whitespace-pre">
{`curl ${api.replace('/api', '')}/api/v1/chat/completions \\
  -H "Authorization: Bearer llmhub_your_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"messages":[{"role":"user","content":"Hello"}]}'`}
                </code>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="api-keys-table-card">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Key Prefix</TableHead>
                      <TableHead>Balance (INR)</TableHead>
                      <TableHead>Providers</TableHead>
                      <TableHead>Rate Limit</TableHead>
                      <TableHead>Requests</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></TableCell></TableRow>
                    ) : keys.length === 0 ? (
                      <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No API keys created yet</TableCell></TableRow>
                    ) : keys.map(k => (
                      <TableRow key={k.id} data-testid={`api-key-row-${k.id}`}>
                        <TableCell className="font-medium text-sm">{k.name}</TableCell>
                        <TableCell><code className="text-xs font-mono bg-muted px-1.5 py-0.5 rounded">{k.key_prefix}</code></TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <span className={`text-sm font-bold ${(k.balance_inr || 0) > 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid={`balance-${k.id}`}>
                              {formatINR(k.balance_inr || 0)}
                            </span>
                            <Button variant="ghost" size="icon" className="h-6 w-6" title="Top Up"
                              onClick={() => { setTopupKeyId(k.id); setTopupOpen(true); }}
                              data-testid={`topup-btn-${k.id}`}>
                              <Plus className="w-3 h-3 text-emerald-600" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-0.5 flex-wrap">
                            {(k.allowed_providers || []).map(p => {
                              const info = providers.find(pr => pr.slug === p);
                              return <Badge key={p} variant="outline" className="text-[9px]" style={{ borderColor: info?.color }}>{info?.name || p}</Badge>;
                            })}
                          </div>
                        </TableCell>
                        <TableCell><span className="text-xs font-mono">{k.rate_limit_per_min}/min</span></TableCell>
                        <TableCell><span className="text-xs font-mono">{k.total_requests || 0}</span></TableCell>
                        <TableCell>
                          <Badge variant={k.enabled ? "default" : "destructive"} className="text-[10px] cursor-pointer" onClick={() => toggleEnabled(k)}
                            data-testid={`status-badge-${k.id}`}>
                            {k.enabled ? "Active" : "Disabled"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openSpending(k.id)} title="View Spending"
                              data-testid={`spending-btn-${k.id}`}>
                              <BarChart3 className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(k)} title="Edit"
                              data-testid={`edit-btn-${k.id}`}>
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleRegenerate(k.id)} title="Regenerate"
                              data-testid={`regen-btn-${k.id}`}>
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => setDeleteId(k.id)} title="Delete"
                              data-testid={`delete-btn-${k.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="spending" className="space-y-4">
          {spendingLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : spendingData ? (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold" data-testid="spending-key-name">Spending: {spendingData.key_name}</h3>
                  <p className="text-sm text-muted-foreground">Balance: <span className="font-bold text-emerald-700">{formatINR(spendingData.balance_inr)} INR</span> | Rate: 1 USD = {spendingData.exchange_rate} INR</p>
                </div>
                <div className="flex gap-1">
                  {["daily", "weekly", "monthly"].map(p => (
                    <Button key={p} variant={spendingPeriod === p ? "default" : "outline"} size="sm"
                      onClick={() => handlePeriodChange(p)} data-testid={`period-${p}`}>
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Spending Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card><CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">Total Spend</p>
                  <p className="text-lg font-bold" data-testid="spending-total-inr">{formatINR(spendingData.totals?.cost_inr)} INR</p>
                  <p className="text-[10px] text-muted-foreground">${spendingData.totals?.cost_usd?.toFixed(4)} USD</p>
                </CardContent></Card>
                <Card><CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">Total Requests</p>
                  <p className="text-lg font-bold" data-testid="spending-total-requests">{spendingData.totals?.requests?.toLocaleString()}</p>
                </CardContent></Card>
                <Card><CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">Total Tokens</p>
                  <p className="text-lg font-bold">{spendingData.totals?.tokens?.toLocaleString()}</p>
                </CardContent></Card>
                <Card><CardContent className="p-3">
                  <p className="text-xs text-muted-foreground">Avg Cost/Request</p>
                  <p className="text-lg font-bold">
                    {spendingData.totals?.requests > 0 ? formatINR(spendingData.totals.cost_inr / spendingData.totals.requests) : "0.00"} INR
                  </p>
                </CardContent></Card>
              </div>

              {/* Spending Chart */}
              {spendingData.timeline?.length > 0 && (
                <Card data-testid="spending-chart-card">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Spend Over Time (INR)</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart data={spendingData.timeline}>
                        <defs>
                          <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10A37F" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#10A37F" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <RechartsTooltip
                          contentStyle={{ fontSize: 12, borderRadius: 8 }}
                          formatter={(val) => [`${formatINR(val)} INR`, "Cost"]}
                        />
                        <Area type="monotone" dataKey="cost_inr" stroke="#10A37F" fill="url(#costGradient)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Provider Breakdown */}
              {spendingData.provider_breakdown && Object.keys(spendingData.provider_breakdown).length > 0 && (
                <Card data-testid="provider-breakdown-card">
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Provider Breakdown</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {Object.entries(spendingData.provider_breakdown).map(([prov, data]) => {
                        const info = providers.find(p => p.slug === prov);
                        return (
                          <div key={prov} className="flex items-center gap-2 p-2 rounded-lg border">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: info?.color || "#888" }} />
                            <div>
                              <p className="text-xs font-medium">{info?.name || prov}</p>
                              <p className="text-sm font-bold">{formatINR(data.cost_inr)} INR</p>
                              <p className="text-[10px] text-muted-foreground">{data.requests} requests</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Recent Transactions */}
              <Card data-testid="transactions-card">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Recent Transactions</CardTitle></CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Time</TableHead>
                          <TableHead>Provider</TableHead>
                          <TableHead>Model</TableHead>
                          <TableHead>Tokens</TableHead>
                          <TableHead>Cost (INR)</TableHead>
                          <TableHead>Latency</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {txLoading ? (
                          <TableRow><TableCell colSpan={6} className="text-center py-6"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></TableCell></TableRow>
                        ) : transactions.length === 0 ? (
                          <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground text-sm">No transactions yet</TableCell></TableRow>
                        ) : transactions.map((tx, i) => {
                          const info = providers.find(p => p.slug === tx.provider);
                          return (
                            <TableRow key={i}>
                              <TableCell className="text-xs text-muted-foreground">{new Date(tx.timestamp).toLocaleString()}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-[9px]" style={{ borderColor: info?.color }}>{info?.name || tx.provider}</Badge>
                              </TableCell>
                              <TableCell className="text-xs font-mono">{tx.model}</TableCell>
                              <TableCell className="text-xs font-mono">{(tx.input_tokens + tx.output_tokens).toLocaleString()}</TableCell>
                              <TableCell className="text-xs font-bold">{formatINR(tx.cost_inr)}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">{tx.latency_ms}ms</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              <Button variant="outline" size="sm" onClick={() => { setActiveTab("keys"); setSelectedKeySpending(null); }}
                data-testid="back-to-keys-btn">
                Back to Keys
              </Button>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Select a key to view spending details</p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editKey ? "Edit API Key" : "Create API Key"}</DialogTitle>
            <DialogDescription>{editKey ? "Update key settings" : "Generate a new API key with wallet"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Key Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Production Backend"
                data-testid="key-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Allowed Providers</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {providers.map(p => (
                  <button key={p.slug}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${form.allowed_providers.includes(p.slug) ? 'bg-primary text-white border-primary' : 'bg-background border-input hover:bg-muted'}`}
                    onClick={() => toggleProvider(p.slug)}
                    data-testid={`provider-toggle-${p.slug}`}>
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                    {p.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rate Limit (req/min)</Label>
                <Input type="number" min={1} value={form.rate_limit_per_min}
                  onChange={e => setForm(f => ({ ...f, rate_limit_per_min: parseInt(e.target.value) || 60 }))}
                  data-testid="rate-limit-input" />
              </div>
              <div className="space-y-2">
                <Label>Monthly Cap (USD)</Label>
                <Input type="number" step="0.01" min={0} value={form.monthly_cap_usd}
                  onChange={e => setForm(f => ({ ...f, monthly_cap_usd: parseFloat(e.target.value) || 100 }))}
                  data-testid="monthly-cap-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-key-btn">{saving ? "Saving..." : editKey ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Key Display */}
      <Dialog open={newKeyDialogOpen} onOpenChange={setNewKeyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Key className="w-5 h-5" /> Your API Key</DialogTitle>
            <DialogDescription>Copy this key now. It will not be shown again.</DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <code className="text-xs font-mono flex-1 break-all select-all" data-testid="new-api-key-value">{newKeyValue}</code>
              <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={copyKey}>
                {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-[11px] text-amber-600 mt-2 font-medium">This key will only be shown once. Store it securely.</p>
          </div>
          <DialogFooter>
            <Button onClick={() => { setNewKeyDialogOpen(false); setNewKeyValue(null); }} data-testid="done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Top-Up Dialog */}
      <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Wallet className="w-5 h-5 text-emerald-600" /> Top Up Wallet</DialogTitle>
            <DialogDescription>Add INR balance to this API key's wallet</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Amount (INR)</Label>
              <Input type="number" min={1} step="1" value={topupAmount}
                onChange={e => setTopupAmount(e.target.value)}
                placeholder="e.g. 500"
                data-testid="topup-amount-input" />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[100, 500, 1000, 5000, 10000].map(amt => (
                <Button key={amt} variant="outline" size="sm" onClick={() => setTopupAmount(String(amt))}
                  data-testid={`topup-preset-${amt}`}>
                  {amt.toLocaleString("en-IN")} INR
                </Button>
              ))}
            </div>
            {topupAmount && parseFloat(topupAmount) > 0 && (
              <p className="text-xs text-muted-foreground">
                Approx. ${(parseFloat(topupAmount) / (spendingSummary?.exchange_rate || 85.5)).toFixed(2)} USD at current rate
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTopupOpen(false)}>Cancel</Button>
            <Button onClick={handleTopup} disabled={topupLoading} data-testid="confirm-topup-btn">
              {topupLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Adding...</> : "Add Balance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete API Key?</DialogTitle><DialogDescription>This will immediately revoke access and discard any remaining balance.</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
